import React, { useState } from "react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../components/ui/select";
import { CategoriesTable } from "@/components/tables/CategoriesTable";
import CategoryDialogForm from "@/components/CategoryDialogForm";
import { Category } from "@/model/entities";
import {
  Card,
  CardHeader,
  CardContent,
  CardAction,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRestaurants } from "@/query/useRestaurants";
import { useCategories } from "@/query/useCategories";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import MenuItemsManagementTab from "./MenuItemsManagementTab";

const ManageMenuPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState("categories");
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(
    null,
  );
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | undefined>(
    undefined,
  );
  const { data: restaurants, isLoading, isError } = useRestaurants();
  const { t } = useTranslation();
  const { deleteCategory } = useCategories(selectedRestaurant ?? "");

  // helper to open edit dialog for a category
  const handleEditCategory = (category: Category) => {
    setCategoryToEdit(category);
    setShowCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryToEdit(undefined);
    setShowCategoryDialog(false);
  };

  const handleAddCategory = () => {
    setCategoryToEdit(undefined);
    setShowCategoryDialog(true);
  };

  // delete handler passed to CategoriesTable: calls mutation and shows toasts
  const handleDeleteCategory = async (category: Category) => {
    if (!category?.id) return;
    try {
      await deleteCategory.mutateAsync(category.id);
      toast.success(t("category.deletedSuccess", "הקטגוריה נמחקה בהצלחה"));
    } catch (err) {
      console.error("Error deleting category", err);
      toast.error(t("category.deletedError", "שגיאה במחיקת קטגוריה"));
    }
  };

  return (
    <div className="container mx-auto py-6" dir="rtl">
      <h1 className="text-2xl font-bold mb-6 text-right">ניהול תפריט</h1>
      <Card>
        <CardHeader>
          <div className="flex flex-col items-start justify-between w-full gap-2">
            <h2 className="card-title">בחירת מסעדה</h2>
            <CardAction>
              {isLoading ? (
                <div className="w-64 text-right">טוען מסעדות...</div>
              ) : isError ? (
                <div className="w-64 text-red-500 text-right">
                  שגיאה בטעינת מסעדות
                </div>
              ) : (
                <Select
                  value={selectedRestaurant ?? ""}
                  onValueChange={setSelectedRestaurant}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="בחר מסעדה" />
                  </SelectTrigger>
                  <SelectContent>
                    {restaurants && restaurants.length > 0 ? (
                      restaurants.map((r) => (
                        <SelectItem key={r.id ?? ""} value={r.id ?? ""}>
                          {r.restaurantName}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-muted-foreground">
                        אין מסעדות זמינות
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </CardAction>
          </div>
        </CardHeader>
        <CardContent>
          {selectedRestaurant ? (
            <Tabs
              value={selectedTab}
              onValueChange={setSelectedTab}
              className="w-full"
            >
              <TabsList className="flex flex-row justify-start mb-4 text-right">
                <TabsTrigger value="categories">קטגוריות</TabsTrigger>
                <TabsTrigger value="menuItems">מנות</TabsTrigger>
              </TabsList>
              <TabsContent value="categories">
                <div className="flex flex-row justify-start mb-4 gap-2">
                  <Button variant="default" onClick={handleAddCategory}>
                    {t("categories.add", "הוסף קטגוריה")}
                  </Button>
                </div>
                <CategoriesTable
                  restaurantId={selectedRestaurant}
                  onEdit={handleEditCategory}
                  onDelete={handleDeleteCategory}
                />
                <CategoryDialogForm
                  open={showCategoryDialog}
                  onClose={handleCloseCategoryDialog}
                  category={categoryToEdit}
                  isAllowedToAddCategory={true}
                  restaurantId={selectedRestaurant ?? undefined}
                />
              </TabsContent>
              <TabsContent value="menuItems">
                <MenuItemsManagementTab restaurantId={selectedRestaurant} />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-right text-muted-foreground py-12">
              אנא בחר מסעדה להצגת התפריט
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageMenuPage;
